import React, { useEffect, useRef, useState, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import moment from 'moment';
import cn from 'classnames';
import { ACTIONS, UI, useAbility } from 'permission';
import {
  Button,
  BUTTON_TYPES,
  Popup,
  Opening,
  AtsSkeleton,
  Card,
  Typography,
  notification,
  NOTIFICATION_TYPES,
} from '_atoms';
import { PopupCloseDeclineOpening, PopupApproveOpening } from '_molecules';
import history from 'helpers/history';
import {
  deleteOpening,
  getOpenings,
  closeOpening as closeOpeningAPI,
  getOpeningsLevelsAPI,
  getOpeningByUuid,
  getJobInfo,
  pauseOpening,
  reopenOpening,
} from 'actions';
import { setOpeningsList } from 'store/reducers/jobReducer';
import type { OpeningProps } from '_atoms/Opening/types';
import type { Opening as OpeningEntity } from 'store/reducers/jobReducer';
import type { AppDispatch } from 'store';
import { ReactComponent as Image } from 'dist/emptyStates/dashboardOpening.svg';
import { JobOpeningsTabFilters } from './JobOpeningsTabFilters';
import { FILTER_OPTIONS, SORTING_OPTIONS } from './constants';
import './style.scss';
import { EXPECTED_DATES } from '../CreateOpening/constants';

type FilterOption = { value: string; label: string; isCustom?: boolean };

interface FiltersState {
  only_my_openings: boolean;
  sorting: (typeof SORTING_OPTIONS)[number];
  statuses: FilterOption[];
  level: FilterOption[];
  levels?: unknown;
}

interface JobOpeningsTabProps {
  showAddOpening?: boolean;
  approveDeclineOpening: (opening: OpeningEntity, decline: boolean, filters: Record<string, unknown>) => void;
  openingsList: OpeningEntity[];
  getListLocations: () => void;
  jobName?: string;
}

interface LevelApiRow {
  level: string;
  is_custom: boolean;
}

export const JobOpeningsTab: React.FC<JobOpeningsTabProps> = ({
  showAddOpening,
  approveDeclineOpening,
  openingsList,
  getListLocations,
  jobName,
}) => {
  const { t } = useTranslation();
  const params = new URLSearchParams(window.location.search);
  const idScrollTo = params.get('scroll');
  const defaultStatuses = params.get('statuses');
  const [scrollTo, setScrollTo] = useState('');
  const ref = useRef<HTMLDivElement | null>(null);
  const { id: jobId } = useParams<{ id: string }>();
  const ability = useAbility();

  const showApproveOpeningPopup = ability.can(ACTIONS.READ, UI.APPROVE_OPENING_POPUP);

  const [closeOpening, setCloseOpening] = useState<OpeningEntity | undefined>(undefined);
  const [openConfirmation, setOpenConfirmation] = useState(false);
  const [currentOpening, setCurrentOpening] = useState<Partial<OpeningEntity>>({});
  const [levelOptions, setLevelOptions] = useState<FilterOption[]>([]);
  const [filters, setFilters] = useState<FiltersState>({
    only_my_openings: false,
    sorting: SORTING_OPTIONS[0],
    statuses: defaultStatuses?.split(',').map(value => ({ value, label: value })) || [
      FILTER_OPTIONS[0],
      FILTER_OPTIONS[1],
      FILTER_OPTIONS[2],
      FILTER_OPTIONS[3],
    ],
    level: [],
  });
  const [openApprovePopup, setOpenApprovePopup] = useState<OpeningEntity | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  const openingsRequestIdRef = useRef(0);

  const dispatch = useDispatch<AppDispatch>();

  const openingsCount = useMemo(() => openingsList.length, [openingsList]);

  const getFilters = () => ({
    status: filters.statuses.map(s => s.value).join(','),
    only_my_openings: filters.only_my_openings,
    sorting_field: 'expected',
    sorting_order: filters.sorting.value,
    level: filters.level
      .filter(s => !s.isCustom)
      .map(s => s.value)
      .join(','),
    custom_level: filters.level
      .filter(s => s.isCustom)
      .map(s => s.value)
      .join(','),
  });

  const onChangeFilters = (field: string, value?: unknown) => {
    if (field === 'sorting') {
      const sorting = filters.sorting === SORTING_OPTIONS[0] ? SORTING_OPTIONS[1] : SORTING_OPTIONS[0];
      setFilters(f => ({ ...f, sorting }));
    } else {
      setFilters(f => ({ ...f, [field]: value }));
    }
  };

  const removeOpening = (uuid: string) => {
    dispatch(deleteOpening(jobId, uuid, getFilters())).finally(() => {
      dispatch(getJobInfo(jobId));
    });
  };

  const onCloseOpening = (closure_comment: string) => {
    dispatch(closeOpeningAPI(jobId, closeOpening!.uuid, { closure_comment }, getFilters()));
    setCloseOpening(undefined);
  };

  const updateOpeningsList = () => {
    openingsRequestIdRef.current += 1;
    const requestId = openingsRequestIdRef.current;
    setLoading(true);

    dispatch(getOpenings(jobId, getFilters(), 'job-openings-list')).finally(() => {
      if (requestId !== openingsRequestIdRef.current) return;
      setLoading(false);
    });
  };

  const onRemoveOpening = (data: OpeningEntity) => {
    setCurrentOpening(data);
    setOpenConfirmation(true);
  };

  const onApproveDeclineOpening = (opening: OpeningEntity, decline: boolean) => {
    const show =
      Math.abs(moment(opening.expected, 'YYYY-MM-DD').diff(moment(), 'days')) <
      Math.abs(EXPECTED_DATES[opening.level]?.diff(moment(), 'days'));
    if (!decline && show && showApproveOpeningPopup) {
      setOpenApprovePopup(opening);
    } else {
      approveDeclineOpening(opening, decline, getFilters());
    }
  };

  const onCloseApproveConfirmation = () => setOpenApprovePopup(undefined);
  const onApproveConfirmationPopup = () => {
    if (!openApprovePopup) return;
    approveDeclineOpening(openApprovePopup, false, getFilters());
    setOpenApprovePopup(undefined);
  };

  const onPauseOpening: OpeningProps['pauseOpening'] = (openingId, data) => {
    dispatch(pauseOpening(jobId, openingId, data as Record<string, unknown>, getFilters()))
      .then(() => {
        notification({ type: NOTIFICATION_TYPES.SUCCESS, message: `The opening has been paused` });
      })
      .catch(() => {});
  };

  const onReopenOpening: OpeningProps['reopenOpening'] = (openingId, data) => {
    dispatch(reopenOpening(jobId, openingId, data as Record<string, unknown>, getFilters()))
      .then(() => {
        notification({
          type: NOTIFICATION_TYPES.SUCCESS,
          message: `The opening is pending reapproval`,
        });
      })
      .catch(() => {});
  };

  const onOpeningAdd = () => history.push(`/jobs/${jobId}/create-opening`);

  const onConfirmDeleteOpening = () => {
    removeOpening(currentOpening.uuid!);
    setOpenConfirmation(false);
    setCurrentOpening({});
  };
  const onCloseDeleteOpening = () => setOpenConfirmation(false);

  const onCloseCloseOpeningPopup = () => setCloseOpening(undefined);

  const onRemoveHighlight = () => setScrollTo('');

  useEffect(() => {
    getOpeningsLevelsAPI(jobId)
      .then(resp => {
        setLevelOptions(
          (resp as unknown as LevelApiRow[]).map(r => ({
            label: r.level,
            value: r.level,
            isCustom: r.is_custom,
          }))
        );
      })
      .catch(() => {
        setLevelOptions([]);
      });
    getListLocations();
    return () => {
      dispatch(setOpeningsList([]));
    };
  }, []);

  const clearSearchParams = () => history.replace({ search: '' });

  const expandStatusFilterToInclude = (status: string) => {
    const matchingOption = FILTER_OPTIONS.find(opt => opt.value.split(',').includes(status));
    if (!matchingOption) return;
    setFilters(f =>
      f.statuses.some(s => s.value === matchingOption.value) ? f : { ...f, statuses: [...f.statuses, matchingOption] }
    );
  };

  useEffect(() => {
    if (!idScrollTo) return;
    getOpeningByUuid(jobId, idScrollTo)
      .then(resp => expandStatusFilterToInclude(resp!.status))
      .catch(clearSearchParams);
  }, []);

  useEffect(() => {
    if (scrollTo && ref.current) {
      ref.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [scrollTo]);

  const onRefChange = React.useCallback((node: HTMLDivElement | null) => {
    if (node) {
      ref.current = node;
      setScrollTo(idScrollTo!);
      clearSearchParams();
    }
  }, []);

  useEffect(updateOpeningsList, [
    filters.only_my_openings,
    filters.statuses,
    filters.level,
    filters.sorting,
    filters.levels,
  ]);

  return (
    <div className="openings-list">
      <JobOpeningsTabFilters
        count={openingsCount}
        filters={filters}
        onChangeFilters={onChangeFilters}
        levelOptions={levelOptions}
      />
      {!loading ? (
        openingsList.length > 0 ? (
          openingsList.map(opening => (
            <div
              key={opening.uuid}
              className="openings-list__opening-wrapper"
              ref={idScrollTo === opening.uuid ? onRefChange : null}
              onBlur={idScrollTo === opening.uuid ? onRemoveHighlight : null}
            >
              <Opening
                className={cn('openings-list__opening', { highlight: scrollTo === opening.uuid })}
                key={opening.uuid}
                closeOpening={setCloseOpening as OpeningProps['closeOpening']}
                removeOpening={onRemoveOpening as OpeningProps['removeOpening']}
                approveDeclineOpening={onApproveDeclineOpening as OpeningProps['approveDeclineOpening']}
                pauseOpening={onPauseOpening}
                reopenOpening={onReopenOpening}
                opening={opening}
                editable
                jobName={jobName}
              />
            </div>
          ))
        ) : (
          <Card className="empty-opening">
            <Image className="empty-opening__image" />
            {showAddOpening ? (
              <>
                <span className="empty-opening__title">
                  {t('translation:job_openings_tab.text_create_opening_to_start')}
                </span>
                <Button type={BUTTON_TYPES.PRIMARY} className="empty-opening__add-opening" onClick={onOpeningAdd}>
                  {t('translation:job_openings_tab.button_add_opening')}
                </Button>
              </>
            ) : (
              <>
                <span className="empty-opening__title">{t('translation:job_openings_tab.text_no_openings_yet')}</span>
                <span className="empty-opening__text">
                  {t('translation:job_openings_tab.text_opening_defines_need')}
                </span>
              </>
            )}
          </Card>
        )
      ) : (
        <div className="opening-loading">
          <AtsSkeleton height={346} />
        </div>
      )}
      <Popup
        open={openConfirmation}
        title={t('translation:job_openings_tab.title_delete_opening')}
        cancelButtonProps={{ onClick: onCloseDeleteOpening, children: t('translation:job_openings_tab.button_cancel') }}
        okButtonProps={{ onClick: onConfirmDeleteOpening, children: t('translation:job_openings_tab.button_delete') }}
      >
        <div className="popup-delete-opening">
          <Typography.Text className="popup-delete-opening__title">{currentOpening.level}</Typography.Text>
          <Typography.Caption className="popup-delete-opening__subtitle">
            {t('translation:job_openings_tab.text_expected_on', {
              date: currentOpening.expected && moment(currentOpening.expected, 'YYYY-MM-DD').format('MMM DD, YYYY'),
            } as Record<string, unknown>)}
          </Typography.Caption>
          <Typography.Text className="popup-delete-opening__text">
            {t('translation:job_openings_tab.text_delete_opening_body')}
          </Typography.Text>
        </div>
      </Popup>
      <PopupCloseDeclineOpening
        open={!!closeOpening}
        opening={closeOpening}
        jobName={jobName!}
        onClose={onCloseCloseOpeningPopup}
        confirm={comment => onCloseOpening(comment)}
        close
      />
      <PopupApproveOpening
        open={!!openApprovePopup}
        onOk={onApproveConfirmationPopup}
        onClose={onCloseApproveConfirmation}
      />
    </div>
  );
};
